import type { useTranslations } from 'next-intl';
import type { AeDeliveryWindowState } from './deliveryWindow';
import type { AeEmirate, AeEmirateGroup } from './emirates';

// Narrowed to the 'ring' namespace (not a generic `(key: string) => string`)
// so passing a translator bound to the wrong namespace — e.g. BottomBarAlternate's
// `t` (namespace 'common') instead of its `tRing` — fails typecheck instead of
// silently falling through to a missing-key string at runtime.
type RingTranslator = ReturnType<typeof useTranslations<'ring'>>;

/** Literal keys per branch, not a lookup table — check-i18n-keys only sees
 * quoted first args to t(), same constraint as useSddNddEligibility.ts. */
export const aeDeliveryEtaCopy = (
  group: AeEmirateGroup,
  windowState: AeDeliveryWindowState,
  t: RingTranslator,
): string => {
  if (windowState === 'error') {
    return t('ring.aeDelivery.lookupError');
  }
  // Emirates without same-day / 4-hour service carry the flat next-day
  // courier promise at every hour — never "not available" copy.
  if (group === 'ineligible') {
    return t('ring.aeDelivery.nextDayAvailable');
  }
  if (windowState === 'same_day') {
    return t('ring.aeDelivery.sameDayAvailable');
  }
  if (windowState === 'next_day') {
    return t('ring.aeDelivery.nextDayAvailable');
  }
  return group === 'dubai'
    ? t('ring.aeDelivery.fourHourOrderBeforeFivePm')
    : t('ring.aeDelivery.fourHourOrderBeforeOnePm');
};

/** Short copy for the bottom bars and order summary — the SDD/NDD bucket
 * only, no "Order now" / cutoff sentence (mirrors India's brief checkout
 * line). same_day and four_hour windows both arrive the same day; the
 * next_day window and ineligible emirates are next-day. */
export const aeDeliveryEtaShortCopy = (
  group: AeEmirateGroup,
  windowState: AeDeliveryWindowState,
  t: RingTranslator,
): string => {
  if (windowState === 'error') {
    return t('ring.aeDelivery.lookupError');
  }
  if (group === 'ineligible' || windowState === 'next_day') {
    return t('ring.aeDelivery.nextDayDelivery');
  }
  return t('ring.aeDelivery.sameDayDelivery');
};

/** Localized display name for a <select> option — same literal-key
 * constraint as above, hence a switch instead of a Record. */
export const aeEmirateLabel = (
  emirate: AeEmirate,
  t: RingTranslator,
): string => {
  switch (emirate) {
    case 'dubai':
      return t('ring.aeDelivery.emirateDubai');
    case 'abu_dhabi':
      return t('ring.aeDelivery.emirateAbuDhabi');
    case 'sharjah':
      return t('ring.aeDelivery.emirateSharjah');
    case 'ajman':
      return t('ring.aeDelivery.emirateAjman');
    case 'ras_al_khaimah':
      return t('ring.aeDelivery.emirateRasAlKhaimah');
    case 'fujairah':
      return t('ring.aeDelivery.emirateFujairah');
    case 'umm_al_quwain':
      return t('ring.aeDelivery.emirateUmmAlQuwain');
  }
};
